import React, { useState, useEffect, useMemo } from 'react'
import { Action } from 'src/Elements/BottomBar/BottomButtonGroup'
import { useDispatch } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTypedSelector } from 'src/State/RootReducer'
import {
  J2Document,
  J2Userprofile,
  CZHome,
  J2Settings,
  C3Physicalswitch,
  K1Label,
  A0dashboard,
  E3more,
  I1Electriccar,
  H1Phonecall,
  F1Couching,
  E1FinalLevel,
  I1ElectricalChargingStation,
} from '@e3dc-react/icons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { appUpdate } from '@e3dc-react/shell/State/App/AppActions'
import { getUserType } from 'src/Libs/user/functions/user-data'
import { UserType, UserTypes } from 'src/Libs/user/types'
import { TICKET_SYSTEM_LINK } from 'src/Configuration/contact'
import useInstallationsContext from 'src/Libs/installations/hooks/use-installations-context'
import { getEMobilityTabRoute } from 'src/Routes/P3_eMobility/routes'
import { getEMobilityTabRouteV1 } from 'src/Routes/P3_eMobility/routes-v1'
import { FeatureElements, getHaveElementsNewContent, useDynamicSystemSettingsElementKeys, useHasNewFeature } from 'src/Libs/new-content'
import { useFeatureFlagsContext } from 'src/Libs/feature-flags/FeatureFlagsContext'
import useFeatureElementsForMoreBottomBar from 'src/Libs/new-content/hooks/use-feature-elements-for-more-bottom-bar'
import { useUser } from 'src/Hooks/useUser'
import { getActivePage } from '../utils/bottom-actions'
import {
  EMobilityImplementation,
  EMobilityImplementationCheckStatus,
  useSelectEMobilityImplementation,
} from 'src/Libs/e-mobility/use-select-e-mobility-implementation'
import useUserVehicles from 'src/Libs/e-mobility/vehicle-integration/hooks/use-user-vehicles'
import { getPropertiesSettingsDetailsRoute } from 'src/Libs/flow/advanced-settings/routes'
import { useInstallationAccessType } from 'src/Libs/installations/hooks/use-installation-access-type'
import { getInstallationDashboardUrlByIds } from 'src/Libs/dashboard/routes'
import useActiveInstallationContext from 'src/Libs/installations/hooks/use-active-installation-context'
import { useShouldEnableEMobilitySection } from 'src/Libs/e-mobility/shared/hooks/use-should-enable-e-mobility-section'

/**
 * Hook to get the actions for the bottom bar for mobile devices for different users as well as the active action
 */
const useUserBottomActions = (): [bottomActions: Action[], activeAction: number, moreActions: Action[]] => {
  const { t } = useCustomTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { userData } = useUser()
  const userLevel = userData?.level
  const [activeAction, setActiveAction] = useState<number>(-1)
  const dispatch = useDispatch()
  const { storageId: activeStorage, installationId: activeInstallation, installation } = useActiveInstallationContext()
  const { installationCount } = useInstallationsContext()
  const {
    featureFlags: { isCustomerReferralEnabled },
  } = useFeatureFlagsContext()
  const { currentFeatureContentVersion } = useHasNewFeature()
  const { featureElements: featureElementsMoreBottomBar } = useFeatureElementsForMoreBottomBar()
  const { systemSettingsElementKeys } = useDynamicSystemSettingsElementKeys()
  const installationAccessType = useInstallationAccessType(installation)

  const pathname = location.pathname
  const locationHash = location.hash

  const userType = useMemo<UserType>(() => {
    if (userLevel) return getUserType(userLevel, installationCount)

    return getUserType('USER', installationCount)
  }, [userLevel, installationCount])

  const moreActive = useTypedSelector(state => state.app.moreActive)
  const {
    eMobilityImplementation,
    isLoading: isEMobilityImplementationLoading,
    status: eMobilityImplementationCheckStatus,
  } = useSelectEMobilityImplementation({
    installationId: activeInstallation,
    skipCheck: Boolean(installation?.decommissionedAt),
  })
  const { shouldEnableEMobilityLink } = useShouldEnableEMobilitySection()

  const { vehicles, hasError } = useUserVehicles(activeInstallation)
  const vehicleConnected = hasError ? false : vehicles && vehicles.length > 0

  const dashboardDisabled = installationCount !== 0 && installationAccessType === 'access-denied'
  const propertiesAdvancedSettingsDisabled = installationAccessType !== 'full-access' && installationAccessType !== 'flow-setup-configurator-access'
  const installationLinksDisabled = installationAccessType !== 'full-access'

  const getBottomActions = (overviewUrl?: string): Action[] => {
    const bottomActions = [
      {
        label: t('framework.dashboard'),
        icon: <CZHome />,
        onClick: () => navigate(getInstallationDashboardUrlByIds(activeInstallation, activeStorage)),
        disabled: dashboardDisabled,
        hasNewContent: getHaveElementsNewContent(currentFeatureContentVersion, [FeatureElements.DashboardSideBarElement]),
        elementKeys: [FeatureElements.DashboardSideBarElement],
      },
      {
        label: t('framework.systemProperties'),
        icon: <J2Settings />,
        onClick: () => navigate(getPropertiesSettingsDetailsRoute(activeInstallation?.toString(), activeStorage)),
        disabled: propertiesAdvancedSettingsDisabled,
        hasNewContent: getHaveElementsNewContent(currentFeatureContentVersion, systemSettingsElementKeys),
        elementKeys: systemSettingsElementKeys,
        id: 'framework-system-properties',
      },
      ...(vehicleConnected
        ? [
            {
              label: t('framework.vehicle-integration'),
              icon: <I1Electriccar />,
              onClick: () => navigate(`/vehicle-integration/${activeInstallation}/vehicle-integration`),
              disabled: installationLinksDisabled,
              hasNewContent: getHaveElementsNewContent(currentFeatureContentVersion, [FeatureElements.VehicleIntegrationSideBarElement]),
              elementKeys: [FeatureElements.VehicleIntegrationSideBarElement],
            },
          ]
        : [
            {
              label: t('framework.eMobility'),
              icon: <I1ElectricalChargingStation />,
              onClick: () => {
                let navigationLink
                /**
                 * There are cases when we are unable to determine if new frontend can be shown(network request to HagerBridge fails) and with that,
                 * which route should be shown initially(dashboard for new e-mobility or charging-sessions for old e-mobility).
                 *
                 * Also, we do not want to keep the user waiting longer than necessary for this information to be fetched, but
                 * let them enter the e-mobility section("/e-mobility") and wait for the request completion there. Redirects are set up for each version
                 * of the frontend so that upon completion, the user will be shown the correct tab.
                 */
                if (isEMobilityImplementationLoading || eMobilityImplementationCheckStatus !== EMobilityImplementationCheckStatus.Success) {
                  if (activeStorage) {
                    navigationLink = `/e-mobility/${activeInstallation}/${activeStorage}`
                  } else {
                    navigationLink = `/e-mobility/${activeInstallation}`
                  }
                } else {
                  navigationLink =
                    eMobilityImplementation === EMobilityImplementation.V1
                      ? getEMobilityTabRouteV1(activeInstallation, activeStorage, 'charging-sessions')
                      : getEMobilityTabRoute(activeInstallation, activeStorage, 'dashboard')
                }

                navigate(navigationLink)
              },
              disabled: !shouldEnableEMobilityLink,
              hasNewContent: getHaveElementsNewContent(currentFeatureContentVersion, [FeatureElements.EMobilitySideBarElement]),
              elementKeys: [FeatureElements.EMobilitySideBarElement],
            },
          ]),
      {
        label: t('framework.more'),
        icon: <E3more />,
        onClick: () => {
          dispatch(appUpdate({ moreActive: !moreActive }))
        },
        disabled: false,
        hasNewContent: getHaveElementsNewContent(currentFeatureContentVersion, featureElementsMoreBottomBar),
        elementKeys: featureElementsMoreBottomBar,
        dataQa: 'button-header-menu',
      },
    ]
    if (overviewUrl) {
      bottomActions.unshift({
        label: t('framework.overview'),
        icon: <A0dashboard />,
        onClick: () => navigate(overviewUrl),
        disabled: false,
        hasNewContent: getHaveElementsNewContent(currentFeatureContentVersion, [FeatureElements.OverviewSideBarElement]),
        elementKeys: [FeatureElements.OverviewSideBarElement],
      })
    }
    return bottomActions
  }

  const getUserMoreActions = (): Action[] => {
    const userMoreActions: Action[] = []
    const userMoreActionsInitial: Action[] = [
      {
        label: t('framework.systemPropeties'),
        icon: <J2Settings />,
        onClick: () => navigate(`/homepowerstation/${activeStorage}/properties`),
      },
      { label: t('homePowerStation.funkActuators'), icon: <C3Physicalswitch />, onClick: () => navigate('/actuators') },
      { label: t('framework.documents'), icon: <J2Document />, onClick: () => navigate('/service/documents') },
      { label: t('framework.contact'), icon: <H1Phonecall />, onClick: () => navigate('/contact') },
      { label: t('framework.inverter-tool'), icon: <E1FinalLevel />, onClick: () => navigate('/inverter-tool') },
      {
        label: t('framework.profile'),
        icon: <J2Userprofile />,
        onClick: () => navigate('/user'),
      },
      {
        label: t('framework.ticketSystem'),
        icon: <K1Label />,
        onClick: () => window.open(TICKET_SYSTEM_LINK, '_blank'),
      },
    ]
    if (isCustomerReferralEnabled)
      userMoreActions.push(
        ...userMoreActionsInitial.slice(0, 2),
        { label: t('framework.referral'), icon: <F1Couching />, onClick: () => navigate('/service/referral') },
        ...userMoreActionsInitial.slice(2)
      )
    return userMoreActions
  }

  const installerMoreActions: Action[] = [
    {
      label: t('framework.profile'),
      icon: <J2Userprofile />,
      onClick: () => navigate('/user'),
    },
  ]
  const serviceMoreActions: Action[] = [
    { label: t('framework.ticketSystem'), icon: <J2Userprofile />, onClick: () => navigate('/administration/users') },
    { label: t('framework.ticketSystem'), icon: <J2Userprofile />, onClick: () => navigate('/administration/ibn') },
    { label: t('framework.ticketSystem'), icon: <J2Userprofile />, onClick: () => navigate('/administration/nda') },
  ]

  const getUserActions = (): [bottomActions: Action[], moreActions: Action[]] => {
    switch (userType) {
      case UserTypes.owner: {
        return [getBottomActions(), getUserMoreActions()]
      }
      case UserTypes.installationOverview: {
        return [getBottomActions('/overview'), getUserMoreActions()]
      }
      case UserTypes.installerPartner:
      case UserTypes.companyOwner: {
        return [getBottomActions('/installer-dashboard'), installerMoreActions]
      }
      case UserTypes.service: {
        return [getBottomActions('/overview/installations'), serviceMoreActions]
      }
      default: {
        return [[], getUserMoreActions()]
      }
    }
  }

  const [actions, setActions] = useState<[Action[], Action[]]>(getUserActions())

  useEffect(() => {
    /* close the more button if location has changed */
    if (moreActive && !locationHash) {
      dispatch(appUpdate({ moreActive: false }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pathname, locationHash])

  useEffect(() => {
    setActiveAction(getActivePage(userType, pathname, Boolean(vehicleConnected)))
  }, [pathname, userType, activeInstallation, activeStorage, vehicleConnected])

  useEffect(() => {
    setActions(getUserActions())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userLevel,
    userType,
    installationCount,
    activeInstallation,
    activeStorage,
    moreActive,
    location,
    currentFeatureContentVersion,
    vehicleConnected,
    installationLinksDisabled,
    propertiesAdvancedSettingsDisabled,
    dashboardDisabled,
    shouldEnableEMobilityLink,
    eMobilityImplementation,
    isEMobilityImplementationLoading,
    eMobilityImplementationCheckStatus,
  ])

  return [actions[0], activeAction, actions[1]]
}

export default useUserBottomActions
